import React from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import '../../styles/ResumeTemplates.css';

// Import images for Word templates
import template1Img from '../../resume_assets/cv-template-0004.jpg';
import template2Img from '../../resume_assets/cv-template-0017.jpg';
import template3Img from '../../resume_assets/cv-template-0020.jpg';

// Word template files
import template1Doc from '../../resume_assets/CV_TEMPLATE_0004.docx';
import template2Doc from '../../resume_assets/CV_TEMPLATE_0017.docx';
import template3Doc from '../../resume_assets/CV_TEMPLATE_0020.docx';

const ResumeTemplates = () => {
  // Word template data
  const wordTemplates = [
    {
      id: 1,
      title: "Professional Business CV Template",
      description: "A clean, professional template ideal for corporate roles and business positions. Features a structured layout with clear sections for experience and skills.",
      image: template1Img,
      file: template1Doc,
      keywords: "professional resume, business CV, corporate template"
    },
    {
      id: 2,
      title: "Creative Modern Resume Template",
      description: "Stand out with this modern resume design featuring a unique layout and visual elements. Perfect for creative industries and design positions.",
      image: template2Img,
      file: template2Doc,
      keywords: "creative resume, modern CV, designer template"
    },
    {
      id: 3,
      title: "Executive CV Template",
      description: "An executive-level resume template with sophisticated styling. Highlights leadership experience and accomplishments with a premium design.",
      image: template3Img,
      file: template3Doc,
      keywords: "executive resume, leadership CV, management template"
    }
  ];

  // Canva template data
  const canvaTemplates = [
    {
      id: 1,
      title: "Blue Minimalist Web Developer Resume",
      description: "A sleek and modern resume template designed for web developers. Features a clean layout with sections for skills, projects, and experience.",
      image: "https://marketplace.canva.com/EAFGgtHWMRQ/3/0/1131w/canva-gnCwiSv9lE0.jpg",
      link: "https://www.canva.com/templates/EAFGgtHWMRQ-blue-minimalist-web-developer-resume/",
      keywords: "web developer resume, minimalist CV, tech resume"
    },
    {
      id: 2,
      title: "Blue Minimalist Software Engineer Resume",
      description: "A simple yet effective resume template for software engineers. Showcases coding skills, projects, and technical expertise with a professional layout.",
      image: "https://marketplace.canva.com/EAE0ApBhGY4/2/0/1131w/canva-DYUaVaN6Og0.jpg",
      link: "https://www.canva.com/templates/EAE0ApBhGY4-blue-minimalist-software-engineer-simple-resume/",
      keywords: "software engineer resume, coding CV, programmer template"
    },
    {
      id: 3,
      title: "Grey and White Minimalist Professional Resume",
      description: "A clean and modern resume template designed for professionals in any field. Features a minimalist design with ample space for your information.",
      image: "https://marketplace.canva.com/EAFezL7Z3X0/3/0/1131w/canva-PrxJrAQvmIM.jpg",
      link: "https://www.canva.com/templates/EAFezL7Z3X0-grey-and-white-minimalist-professional-resume/",
      keywords: "professional resume, minimalist CV, general template"
    }
  ];

  return (
    <Container className="resume-templates-container page-container">
      <div className="text-center mb-5 py-4">
        <h1 className="section-title">Professional Resume Templates</h1>
        <p className="lead mx-auto" style={{ maxWidth: '800px' }}>
          Download free, professionally designed resume templates to help you land your dream job. 
          Choose from Microsoft Word or Canva templates that are ready to customize.
        </p>
      </div>

      {/* Google Ads Placeholder - Top */}
      <div className="google-ads-container mb-5">
        <div className="ad-placeholder">
          <p className="ad-text">Advertisement</p>
          <div className="ad-content">Your Advertisement here</div>
        </div>
      </div>

      {/* Word Templates Section */}
      <section className="templates-section mb-5">
        <div className="section-header">
          <h2 className="section-subtitle">Microsoft Word Resume Templates</h2>
          <div className="section-underline"></div>
        </div>
        <p className="section-description">
          Download these premium-quality Microsoft Word resume templates. Fully editable and ready to customize 
          to match your skills and experience. Simply download, open in Word, and start editing.
        </p>
        
        <Row className="mt-4">
          {wordTemplates.map(template => (
            <Col lg={4} md={6} className="mb-4" key={template.id}>
              <Card className="template-card h-100 shadow-sm">
                <div className="template-image-container">
                  <Card.Img 
                    variant="top" 
                    src={template.image} 
                    alt={`${template.title} - Resume Template Preview`} 
                    className="template-image"
                  />
                </div>
                <Card.Body>
                  <Card.Title className="template-title">{template.title}</Card.Title>
                  <Card.Text className="template-description">
                    {template.description}
                  </Card.Text>
                </Card.Body>
                <Card.Footer className="bg-white border-0">
                  <a 
                    href={template.file} 
                    download={`Resume_Template_${template.id}.docx`}
                    className="btn btn-primary w-100"
                  >
                    <i className="la la-download me-2" aria-hidden="true"></i>
                    Download DOCX
                  </a>
                </Card.Footer>
              </Card>
            </Col>
          ))}
        </Row>
      </section>

      {/* Google Ads Placeholder - Middle */}
      <div className="google-ads-container mb-5">
        <div className="ad-placeholder">
          <p className="ad-text">Advertisement</p>
          <div className="ad-content">Google Ads</div>
        </div>
      </div>

      {/* Canva Templates Section */}
      <section className="templates-section mb-5">
        <div className="section-header">
          <h2 className="section-subtitle">Canva Resume Templates</h2>
          <div className="section-underline"></div>
        </div>
        <p className="section-description">
          Use these pre-designed Canva templates to create a stunning resume online. 
          These templates offer modern designs with easy customization options directly in Canva's user-friendly platform.
        </p>
        
        <Row className="mt-4">
          {canvaTemplates.map(template => (
            <Col lg={4} md={6} className="mb-4" key={template.id}>
              <Card className="template-card h-100 shadow-sm">
                <div className="template-image-container">
                  <Card.Img 
                    variant="top" 
                    src={template.image} 
                    alt={`${template.title} - Canva Resume Template Preview`} 
                    className="template-image"
                  />
                </div>
                <Card.Body>
                  <Card.Title className="template-title">{template.title}</Card.Title>
                  <Card.Text className="template-description">
                    {template.description}
                  </Card.Text>
                </Card.Body>
                <Card.Footer className="bg-white border-0">
                  <a 
                    href={template.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary w-100"
                  >
                    <i className="la la-external-link-alt me-2" aria-hidden="true"></i>
                    Use in Canva
                  </a>
                </Card.Footer>
              </Card>
            </Col>
          ))}
        </Row>
      </section>

      {/* Google Ads Placeholder - Before Tips */}
      <div className="google-ads-container mb-5">
        <div className="ad-placeholder">
          <p className="ad-text">Advertisement</p>
          <div className="ad-content">Your Advertisement here</div>
        </div>
      </div>

      <section className="resume-tips-section mb-5">
        <div className="section-header">
          <h2 className="section-subtitle">Resume Writing Tips & ATS Optimization</h2>
          <div className="section-underline"></div>
        </div>
        <div className="resume-tips-content card shadow-sm p-4">
          <p className="tips-intro">
            A well-crafted, ATS-optimized resume significantly increases your chances of landing an interview. 
            Follow these expert tips to create a resume that impresses both automated systems and human recruiters:
          </p>
          
          <div className="tips-container">
            <div className="tips-column">
              <h3 className="tips-heading">ATS Compliance Essentials</h3>
              <ul className="tips-list">
                <li><strong>Use standard section headings</strong> - "Work Experience," "Education," and "Skills" are easily recognized by ATS software.</li>
                <li><strong>Avoid complex formatting</strong> - Tables, text boxes, headers/footers, and graphics can confuse ATS systems.</li>
                <li><strong>Choose compatible file formats</strong> - Submit in .docx or .pdf format unless otherwise specified (with .docx generally being safer).</li>
                <li><strong>Use standard fonts</strong> - Stick with Arial, Calibri, Helvetica, or Times New Roman in 10-12pt size.</li>
                <li><strong>Include keywords from the job description</strong> - ATS systems scan for relevant terms that match the job requirements.</li>
                <li><strong>Use conventional file naming</strong> - Name your file "FirstName_LastName_Resume" for easy identification.</li>
              </ul>
              
              <h3 className="tips-heading">Content Best Practices</h3>
              <ul className="tips-list">
                <li><strong>Tailor to each job</strong> - Customize your resume for each position by matching your qualifications to job requirements.</li>
                <li><strong>Use powerful action verbs</strong> - Begin achievement statements with verbs like "developed," "implemented," or "increased."</li>
                <li><strong>Quantify achievements</strong> - Include specific numbers and percentages that demonstrate your impact (e.g., "increased sales by 20%").</li>
                <li><strong>Focus on results</strong> - Emphasize outcomes and accomplishments rather than just listing responsibilities.</li>
                <li><strong>Avoid first-person language</strong> - Remove pronouns like "I" and "my" for a more professional tone.</li>
              </ul>
            </div>
            
            <div className="tips-column">
              <h3 className="tips-heading">Structure and Organization</h3>
              <ul className="tips-list">
                <li><strong>Keep it concise</strong> - Aim for 1-2 pages maximum, depending on your experience level.</li>
                <li><strong>Use consistent formatting</strong> - Maintain uniform fonts, spacing, and bullet styles throughout.</li>
                <li><strong>Include white space</strong> - Create a clean, readable document that isn't overcrowded with text.</li>
                <li><strong>Place important information first</strong> - Most relevant details should appear in the top third of your resume.</li>
                <li><strong>Use bullet points</strong> - Create scannable content with 3-6 bullets per position.</li>
              </ul>
              
              <h3 className="tips-heading">Essential Sections</h3>
              <ul className="tips-list">
                <li><strong>Contact information</strong> - Name, phone, professional email, LinkedIn URL, and location (city/state).</li>
                <li><strong>Professional summary</strong> - 3-5 lines highlighting your qualifications, experience, and value proposition.</li>
                <li><strong>Core skills</strong> - A dedicated section listing 8-12 relevant hard and soft skills.</li>
                <li><strong>Work experience</strong> - Company names, job titles, dates, and achievement-focused bullet points.</li>
                <li><strong>Education</strong> - Degrees, institutions, graduation dates, and relevant honors or coursework.</li>
                <li><strong>Additional sections</strong> - Consider including certifications, projects, or relevant volunteer work when applicable.</li>
              </ul>
            </div>
          </div>
          
          <div className="ats-example-box mt-4">
            <h4 className="ats-example-heading">ATS-Friendly Example:</h4>
            <div className="ats-example-content">
              <p><strong>Original bullet point:</strong> "Helped the sales team achieve targets by creating reports"</p>
              <p><strong>ATS-optimized version:</strong> "Increased sales team efficiency by 25% through development of automated performance dashboards that tracked key metrics and identified growth opportunities"</p>
              <p className="ats-example-explanation">The optimized version includes specific metrics, relevant keywords (automated, performance, metrics), and clearly demonstrates impact.</p>
            </div>
          </div>
          
          <p className="tips-conclusion mt-4">
            Remember that your resume must first pass through ATS filters before reaching human eyes. By following these ATS compliance tips and best practices, you'll create a resume that successfully navigates automated systems while effectively showcasing your qualifications to hiring managers.
          </p>
        </div>
      </section>

      <div className="call-to-action text-center mt-5 py-5 card shadow-sm">
        <h3 className="mb-3">Ready to perfect your job application?</h3>
        <p className="mx-auto" style={{ maxWidth: '700px' }}>
          Once you've created your resume, use our other tools to complete your job application process.
        </p>
        <div className="cta-buttons mt-4">
          <Button as={Link} to="/tools/resume-builder" className="me-3 px-4 py-2">
            Build Your Resume with AI
          </Button>
          <Button as={Link} to="/tools/resume-analyzer" variant="outline-primary" className="px-4 py-2">
            Check Resume ATS Compatibility
          </Button>
        </div>
      </div>

      {/* Google Ads Placeholder - Bottom */}
      <div className="google-ads-container my-5">
        <div className="ad-placeholder">
          <p className="ad-text">Advertisement</p>
          <div className="ad-content">Your Advertisement here</div>
        </div>
      </div>
    </Container>
  );
};

export default ResumeTemplates;